import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KeycloakService } from 'keycloak-angular';
import { SideEntityFormComponent as ParentSideEntityFormComponent } from '@shared-lib/chart-components/side-entity-form/side-entity-form.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { catchError, map, Observable, of } from 'rxjs';
import { getEntityFollowUpSaveHandler } from './save-handlers/entity-follow-up-save-handlers';

const DATE_DISPLAY_FORMATS = {
  parse: { dateInput: null },
  display: {
    dateInput: { month: '2-digit', day: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { month: 'long', day: 'numeric', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
};

@Component({
  selector: 'app-side-entity-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DATE_DISPLAY_FORMATS },
  ],
  templateUrl: './side-entity-form.component.html',
  styleUrls: ['./side-entity-form.component.scss'],
})
export class SideEntityFormComponent extends ParentSideEntityFormComponent {
  private static readonly DEBUG_LOG_KEY = 'PALANTIR_CONFIG_SAVE_DEBUG_V2';
  private static readonly TABLE_REFRESH_EVENT = 'palantir-table-refresh-request';
  private static readonly HEADER_ATTRIBUTE_BASE_PATH = 'assets/header-attribute';
  private static readonly ALWAYS_HIDDEN_FIELD_TOKENS = new Set(['cpm']);
  private static readonly HIDDEN_FIELD_TOKENS = new Set(['is valid', 'created by', 'created date', 'updated by', 'updated date', 'uuid', 'cpm']);
  private static readonly FUEL_RATING_DEFINITION_FIELD_ORDER = new Map([
    ['range description', 0],
    ['minimum horsepower', 1],
    ['maximum horsepower', 2],
    ['active indicator', 3],
  ]);
  private static readonly RER_PA_HISTORY_FIELD_ORDER = new Map([
    ['code', 0],
    ['pricing action', 1],
    ['change reason', 2],
    ['old price', 3],
    ['suggested future price', 4],
    ['new price', 5],
    ['new effective date', 6],
    ['notes', 7],
  ]);
  private static readonly ENTITY_HIDDEN_FIELD_TOKENS = new Map<string, Set<string>>([
    ['cu_rer_pa_history', new Set(['region', 'regional pricing flag'])],
  ]);
  private readonly customHttp: HttpClient;
  private readonly customCdr: ChangeDetectorRef;
  private readonly headerOrderCache = new Map<string, string[] | null>();
  private readonly keycloakServiceOverride: KeycloakService = inject(KeycloakService);

  constructor(
    globalFilterService: GlobalFilterService,
    http: HttpClient,
    configService: ConfigService,
    snackBar: MatSnackBar,
    responsiveLayoutService: ResponsiveLayoutService,
    cdr: ChangeDetectorRef,
  ) {
    super(
      globalFilterService,
      http,
      configService,
      snackBar,
      responsiveLayoutService,
      cdr,
    );
    this.customHttp = http;
    this.customCdr = cdr;
  }

  private readonly dateValueCache = new Map<string, Date | null>();

  /**
   * Returns a stable Date reference for a given stored value so the date
   * input's [value] binding does not see a "new" object on every change
   * detection cycle. Without this, an already-populated date would be
   * re-written into the input mid-edit, making it impossible to type over.
   */
  override getDateValue(value: any): Date | null {
    if (value instanceof Date) {
      return super.getDateValue(value);
    }

    const key = value === null || value === undefined ? '' : String(value);
    if (this.dateValueCache.has(key)) {
      return this.dateValueCache.get(key) ?? null;
    }

    const resolved = super.getDateValue(value);
    this.dateValueCache.set(key, resolved);
    return resolved;
  }

  /**
   * Masks manual keyboard entry in the date inputs so users can type the
   * date as numbers and have it auto-formatted to MM/DD/YYYY. Non-digit
   * characters are stripped, and slashes are inserted automatically. The
   * datepicker still parses the completed value on blur via (dateChange).
   */
  onDateInputMask(fieldKey: string, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    if (input.value !== formatted) {
      input.value = formatted;
    }

    // When the field is cleared, null the model immediately so the save
    // sends null instead of relying on the datepicker's blur event.
    if (!digits) {
      this.onFieldValueChange(fieldKey, null);
    }
  }

  private getBottomRightSnackBarConfig(
    duration = 10000,
    panelClass?: string[],
  ): MatSnackBarConfig {
    return {
      duration,
      horizontalPosition: 'end',
      verticalPosition: 'bottom',
      panelClass,
    };
  }

  protected override generateFormFieldsFromRecord(): void {
    super.generateFormFieldsFromRecord();
    this.filterHiddenFormFields();
    this.applyConfiguredFieldOrder();
    this.applyKnownFieldOrderFallback();
  }

  override onFieldDropdownOpened(fieldKey: string, isOpen = true): void {
    if (!isOpen || !fieldKey) {
      return;
    }

    const component = this as any;
    const config = component.getFieldDropdownConfig(fieldKey);
    if (!config?.apiUrl) {
      return;
    }

    component.fieldDropdownLoading[fieldKey] = true;
    const payload = component.buildFieldDropdownPayload(fieldKey, config);

    component.configService.getDropdownOptions(
      config.apiUrl,
      payload,
      config.method || 'post',
      config.headers || this.data?.headers,
    ).subscribe({
      next: (response: any) => {
        component.fieldDropdownOptions[fieldKey] = this.normalizeDropdownOptionsForField(
          response,
          fieldKey,
        );
        component.fieldDropdownLoading[fieldKey] = false;
      },
      error: () => {
        component.fieldDropdownOptions[fieldKey] = [];
        component.fieldDropdownLoading[fieldKey] = false;
      },
    });
  }

  override getFieldDisplayLabel(fieldKey: string): string {
    const value = (this as any).editableRecord?.[fieldKey];
    const dropdown = (this as any).getFieldDropdownConfig(fieldKey);

    if (!dropdown) {
      return this.formatFieldValueForField(fieldKey, value);
    }

    const selectedValue = this.getFieldSelectedValue(value, dropdown);
    const selectedOption = ((this as any).fieldDropdownOptions?.[fieldKey] || []).find((option: any) =>
      this.compareFieldDropdownValues(this.getFieldDropdownValue(option), selectedValue),
    );

    if (selectedOption) {
      return this.getFieldDropdownLabelForField(fieldKey, selectedOption);
    }

    return this.formatFieldValueForField(fieldKey, value);
  }

  getFieldDisplayLabelForField(fieldKey: string): string {
    const value = (this as any).editableRecord?.[fieldKey];
    const dropdown = (this as any).getFieldDropdownConfig(fieldKey);
    const selectedValue = this.getFieldSelectedValue(value, dropdown);
    const selectedOption = (this as any).fieldDropdownOptions?.[fieldKey]?.find((option: any) =>
      this.compareFieldDropdownValues(this.getFieldDropdownValue(option), selectedValue),
    );

    if (selectedOption) {
      return this.getFieldDropdownLabelForField(fieldKey, selectedOption);
    }

    return this.formatFieldValueForField(fieldKey, value);
  }

  override getFieldDropdownLabel(option: any): string {
    return this.formatAttributeLabel(option);
  }

  override getFieldDropdownValue(option: any): any {
    if (!option || typeof option !== 'object') {
      return option;
    }

    return option?.data ?? option;
  }

  getFieldDropdownLabelForField(_fieldKey: string, option: any): string {
    return this.getFieldDropdownLabel(option);
  }

  private formatFieldValueForField(_fieldKey: string, value: any): string {
    return this.formatAttributeLabel(value);
  }

  override saveForm(action: any): void {
    if (!action.apiUrl) {
      console.error('Save API URL is not defined.');
      return;
    }

    const component = this as any;
    this.applyNumericFormattingOnSave(component.editableRecord);
    component.isSaving = true;
    const method = action.method || 'POST';
    const configuredSaveTemplate = action?.savePayload || action?.customPayload || action?.rawPayload;

    const isNewRecord =
      component.originalRecord === null ||
      component.originalRecord === undefined ||
      component.originalRecord.id === null;

    const primaryId =
      component.originalRecord?.Code ??
      component.originalRecord?.code ??
      component.editableRecord?.Code ??
      component.editableRecord?.code ??
      null;

    if (!configuredSaveTemplate && !isNewRecord && (primaryId === null || primaryId === undefined || primaryId === '')) {
      component.isSaving = false;
      component.snackBar.open(
        'Cannot save: primaryId (Code) is missing.',
        'Close',
        this.getBottomRightSnackBarConfig(),
      );
      return;
    }

    const buildChangedFields = (editable: any, original: any): Record<string, any> => {
      const changed: Record<string, any> = {};
      const editableKeys = Object.keys(editable || {});
      for (const key of editableKeys) {
        if (!component.valuesEqualForSave(editable?.[key], original?.[key])) {
          changed[key] = this.normalizeFieldValueForPayload(editable?.[key]);
        }
      }
      return changed;
    };

    const changedFields = isNewRecord ? null : buildChangedFields(component.editableRecord, component.originalRecord);
    let hasChanges = false;

    if (!isNewRecord) {
      hasChanges = !!changedFields && Object.keys(changedFields).length > 0;
    } else {
      hasChanges = Object.keys(component.editableRecord || {}).length > 0;
    }

    if (!hasChanges) {
      component.isSaving = false;
      component.snackBar.open(
        'No changes to save.',
        'Close',
        this.getBottomRightSnackBarConfig(),
      );
      return;
    }

    const dataForCreate = this.normalizeRecordForSave(component.editableRecord);
    delete (dataForCreate as any).id;

    const configuredSavePayload = this.buildConfiguredSavePayloadWithDynamicEntity(
      action,
      isNewRecord,
      changedFields,
    );
    const payload = configuredSavePayload || (isNewRecord
      ? { data: dataForCreate }
      : { primaryId, data: this.normalizeRecordForSave(component.editableRecord) });

    const globalFilters = component.globalFilterService.getCurrentFilters();
    const queryParams = { ...(globalFilters?.queryParams || {}) };
    if (!configuredSavePayload && !queryParams['entity']) {
      const entityFromRequestParams = globalFilters?.requestParams?.['entity'];
      if (entityFromRequestParams) {
        queryParams['entity'] = entityFromRequestParams;
      }
    }

    const filtersToSend = configuredSavePayload ? {} : { queryParams, requestParams: payload };
    const requestConfig = configuredSavePayload
      ? {
          ...component.data,
          ...action,
          rawPayload: payload,
          skipDefaultPayload: true,
          embedQueryParams: false,
          embeddedParams: [],
          useQueryParam: false,
        }
      : { ...component.data, ...action, skipDefaultPayload: true };

    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    this.debugLog('save_payload_built', {
      selectedEntityKey,
      payloadRequestEntity: payload?.requestData?.entity,
      payload,
    });

    const followUpHandler = getEntityFollowUpSaveHandler(selectedEntityKey);
    if (followUpHandler?.skipGenericSave) {
      // For this entity, skip the generic GenericSaveService call and save
      // directly via the follow-up function using the current form data.
      const directFollowUpPayload = this.buildFollowUpSavePayload(
        null,
        configuredSaveTemplate,
        selectedEntityKey,
        action,
        payload?.requestData,
      );
      const directRequestConfig = {
        ...requestConfig,
        rawPayload: directFollowUpPayload,
      };

      this.debugLog('save_skip_generic_dispatch', {
        selectedEntityKey,
        directFollowUpPayload,
      });

      this.executeConfiguredSaveRequest(action.apiUrl, {}, directRequestConfig, method).subscribe({
        next: (response: any) => {
          this.debugLog('save_skip_generic_success', { selectedEntityKey, response });
          this.finishSuccessfulSave(action, selectedEntityKey, isNewRecord);
        },
        error: (error: any) => {
          component.isSaving = false;
          console.error('Save failed', error);
          component.snackBar.open(
            error?.message ? String(error.message) : 'Error saving data.',
            'Close',
            this.getBottomRightSnackBarConfig(
              error?.message ? 10000 : undefined,
              error?.message ? ['error-snackbar'] : undefined,
            ),
          );
        },
      });
      return;
    }

    this.executeConfiguredSaveRequest(action.apiUrl, filtersToSend, requestConfig, method).subscribe({
      next: (response: any) => {
        const sequentialRequired = this.requiresSequentialFollowUpSave(selectedEntityKey);
        this.debugLog('save_primary_success', {
          selectedEntityKey,
          sequentialRequired,
          response,
        });

        if (!sequentialRequired) {
          this.finishSuccessfulSave(action, selectedEntityKey, isNewRecord);
          return;
        }

        const followUpPayload = this.buildFollowUpSavePayload(
          response,
          configuredSaveTemplate,
          selectedEntityKey,
          action,
        );
        const followUpRequestConfig = {
          ...requestConfig,
          rawPayload: followUpPayload,
        };

        this.debugLog('save_follow_up_dispatch', {
          selectedEntityKey,
          followUpPayload,
        });

        this.executeConfiguredSaveRequest(action.apiUrl, {}, followUpRequestConfig, method).subscribe({
          next: (followUpResponse: any) => {
            this.debugLog('save_follow_up_success', {
              selectedEntityKey,
              response: followUpResponse,
            });
            this.finishSuccessfulSave(action, selectedEntityKey, isNewRecord);
          },
          error: (error: any) => {
            component.isSaving = false;
            console.error('Follow-up save failed', error);
            component.snackBar.open(
              'Error saving data.',
              'Close',
              this.getBottomRightSnackBarConfig(),
            );
          },
        });
      },
      error: (error: any) => {
        component.isSaving = false;
        console.error('Save failed', error);
        if (error?.message) {
          component.snackBar.open(
            String(error.message),
            'Close',
            this.getBottomRightSnackBarConfig(10000, ['error-snackbar']),
          );
          return;
        }
        component.snackBar.open(
          'Error saving data.',
          'Close',
          this.getBottomRightSnackBarConfig(),
        );
      },
    });
  }

  private buildConfiguredSavePayloadWithDynamicEntity(
    action: any,
    isNewRecord: boolean,
    changedFields: Record<string, any> | null,
  ): any | null {
    const component = this as any;
    const template = action?.savePayload || action?.customPayload || action?.rawPayload;
    if (!template) {
      return null;
    }

    const payload = JSON.parse(JSON.stringify(template));
    const requestDataValue = this.buildFullRowSaveValue(isNewRecord, changedFields);
    payload.requestData = {
      ...(payload.requestData || {}),
      ...(requestDataValue && typeof requestDataValue === 'object' ? requestDataValue : {}),
    };
    if (payload?.requestData && Object.prototype.hasOwnProperty.call(payload.requestData, 'value')) {
      delete payload.requestData.value;
    }
    payload.requestData = this.normalizeUuidFieldsForPayload(payload.requestData);

    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    if (selectedEntityKey) {
      payload.requestData.entity = selectedEntityKey;
    }

    payload.functionName = payload.functionName || action?.functionName || component.data?.functionName || 'GenericSaveService';
    payload.entity = payload.entity || component.data?.entity || 'product';
    payload.workflowId = payload.workflowId || component.data?.workflowId || 'UI';
    payload.communicationType = payload.communicationType || 'rest';
    payload.version = payload.version || action?.version || component.data?.version || '1.0';

    if (payload.functionName === 'GenericSaveService') {
      const userEmail = this.getLoggedInUserEmailForOverride();
      this.debugLog('save_user_email_resolved', {
        userEmail,
        requestDataBeforeAssign: payload.requestData,
      });

      if (userEmail) {
        payload.requestData = {
          ...(payload.requestData || {}),
          user_id: userEmail,
        };
        this.debugLog('save_user_id_assigned', {
          requestDataAfterAssign: payload.requestData,
        });
      } else {
        this.debugLog('save_user_email_missing', {
          requestDataUnchanged: payload.requestData,
        });
      }
    }

    if (!Array.isArray(payload.endpoints) || payload.endpoints.length === 0) {
      payload.endpoints = [
        {
          functionName: payload.functionName,
          version: payload.version,
        },
      ];
    } else {
      payload.endpoints = payload.endpoints.map((endpoint: any, index: number) => {
        if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
          return endpoint;
        }

        return {
          ...endpoint,
          functionName: endpoint.functionName || payload.functionName,
          version: endpoint.version || payload.version,
        };
      });
    }

    this.debugLog('save_payload_entity_applied', {
      selectedEntityKey,
      payloadRequestEntity: payload.requestData.entity,
      payloadRequestData: payload.requestData,
    });

    return payload;
  }

  private getLoggedInUserEmailForOverride(): string | null {
    const keycloak = this.keycloakServiceOverride.getKeycloakInstance();
    const tokenParsed: any = keycloak?.tokenParsed ?? {};
    const preferredUsername =
      typeof tokenParsed?.preferred_username === 'string'
        ? tokenParsed.preferred_username.trim()
        : '';

    const emailCandidates = [
      tokenParsed?.email,
      preferredUsername.includes('@') ? preferredUsername : null,
    ];

    for (const candidate of emailCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        this.debugLog('save_keycloak_email_candidate_selected', {
          email: candidate.trim(),
          tokenParsed,
        });
        return candidate.trim();
      }
    }

    this.debugLog('save_keycloak_email_missing', {
      tokenParsed,
      subject: keycloak?.subject,
    });
    return null;
  }

  private applyConfiguredFieldOrder(): void {
    const entityKey = this.resolveSelectedEntityKeyForHeaderOrder();
    if (!entityKey || !Array.isArray(this.formFields) || !this.formFields.length) {
      return;
    }

    this.loadHeaderOrder(entityKey).subscribe((configuredOrder: string[] | null) => {
      if (!configuredOrder?.length || !Array.isArray(this.formFields) || !this.formFields.length) {
        return;
      }

      const orderIndex = new Map<string, number>();
      configuredOrder.forEach((fieldKey, index) => {
        orderIndex.set(this.normalizeOrderToken(fieldKey), index);
      });

      this.formFields = [...this.formFields].sort((left, right) => {
        const leftIndex = this.resolveConfiguredOrderIndex(left, orderIndex);
        const rightIndex = this.resolveConfiguredOrderIndex(right, orderIndex);

        if (leftIndex !== undefined && rightIndex !== undefined) {
          return leftIndex - rightIndex;
        }

        if (leftIndex !== undefined) {
          return -1;
        }

        if (rightIndex !== undefined) {
          return 1;
        }

        return 0;
      });

      this.applyKnownFieldOrderFallback();
      this.customCdr.detectChanges();
    });
  }

  private resolveConfiguredOrderIndex(field: any, orderIndex: Map<string, number>): number | undefined {
    const candidates = [
      field?.key,
      field?.label,
    ];

    for (const candidate of candidates) {
      const normalizedCandidate = this.normalizeOrderToken(candidate);
      if (!normalizedCandidate) {
        continue;
      }

      const matchedIndex = orderIndex.get(normalizedCandidate);
      if (matchedIndex !== undefined) {
        return matchedIndex;
      }
    }

    return undefined;
  }

  private normalizeOrderToken(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private resolveSelectedEntityKeyForHeaderOrder(): string | null {
    const selectedEntityKey = this.resolveSelectedEntityKeyForSave();
    return selectedEntityKey ? String(selectedEntityKey).trim() : null;
  }

  private loadHeaderOrder(entityKey: string): Observable<string[] | null> {
    const normalizedEntityKey = String(entityKey || '').trim();
    if (!normalizedEntityKey) {
      return of(null);
    }

    if (this.headerOrderCache.has(normalizedEntityKey)) {
      return of(this.headerOrderCache.get(normalizedEntityKey) ?? null);
    }

    const primaryUrl = `${SideEntityFormComponent.HEADER_ATTRIBUTE_BASE_PATH}/${normalizedEntityKey}.json`;
    const legacyUrl = `${SideEntityFormComponent.HEADER_ATTRIBUTE_BASE_PATH}/${normalizedEntityKey}/order.json`;

    return this.customHttp.get(primaryUrl).pipe(
      catchError(() => this.customHttp.get(legacyUrl)),
      map((response: any) => {
        const configuredOrder = this.parseHeaderOrder(response);
        this.headerOrderCache.set(normalizedEntityKey, configuredOrder);
        return configuredOrder;
      }),
      catchError(() => {
        this.headerOrderCache.set(normalizedEntityKey, null);
        return of(null);
      }),
    );
  }

  private parseHeaderOrder(config: any): string[] | null {
    const configuredOrder = Array.isArray(config)
      ? config
      : config?.columnOrder ?? config?.fieldOrder ?? config?.order;

    if (!Array.isArray(configuredOrder)) {
      return null;
    }

    const normalizedOrder = configuredOrder
      .map((value: any) => String(value || '').trim())
      .filter(Boolean);

    return normalizedOrder.length ? normalizedOrder : null;
  }

  private buildFullRowSaveValue(
    isNewRecord: boolean,
    changedFields: Record<string, any> | null,
  ): Record<string, any> {
    const component = this as any;
    const baseRow = this.normalizeRecordForSave(component.originalRecord);
    const editedRow = this.normalizeRecordForSave(component.editableRecord);
    const normalizedChangedFields =
      changedFields && typeof changedFields === 'object'
        ? this.normalizeRecordForSave(changedFields)
        : {};
    const fullRow = isNewRecord
      ? editedRow
      : {
          ...baseRow,
          ...editedRow,
          ...(normalizedChangedFields && typeof normalizedChangedFields === 'object' ? normalizedChangedFields : {}),
        };

    delete (fullRow as any).id;
    delete (fullRow as any).__isDraftNewRecord;

    const identifier =
      component.originalRecord?.uuid ??
      component.editableRecord?.uuid ??
      component.originalRecord?.id ??
      component.editableRecord?.id ??
      null;

    if (fullRow['uuid'] === undefined) {
      fullRow['uuid'] = identifier ?? null;
    }
    this.normalizeUuidFieldOnRecord(fullRow);

    this.debugLog('save_full_row_value_built', {
      isNewRecord,
      changedFieldKeys: Object.keys(changedFields || {}),
      originalRow: baseRow,
      editedRow,
      payloadValue: fullRow,
    });

    return fullRow;
  }

  private normalizeRecordForSave(record: any): Record<string, any> {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return {};
    }

    const normalized: Record<string, any> = {};
    Object.keys(record).forEach((key) => {
      normalized[key] = this.normalizeFieldValueForPayload(record[key]);
    });
    delete normalized['__isDraftNewRecord'];
    this.normalizeUuidFieldOnRecord(normalized);

    this.debugLog('record_normalized_for_save', {
      inputKeys: Object.keys(record),
      blankStringKeysConvertedToNull: Object.keys(normalized).filter((key) => normalized[key] === null && this.isBlankString(record[key])),
      normalized,
    });

    return normalized;
  }

  private normalizeFieldValueForPayload(value: any): any {
    const component = this as any;
    const normalizedValue = component.normalizeValueForSave(value);
    const roundedValue = this.roundNumericValueForSave(normalizedValue);
    const sanitizedValue = this.sanitizeEmptyValues(roundedValue);

    if (this.containsBlankString(normalizedValue)) {
      this.debugLog('blank_value_sanitized', {
        originalValue: value,
        normalizedValue,
        sanitizedValue,
      });
    }

    return sanitizedValue;
  }

  private normalizeUuidFieldsForPayload(payload: any): any {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return payload;
    }

    const normalizedPayload = { ...payload };
    this.normalizeUuidFieldOnRecord(normalizedPayload);
    return normalizedPayload;
  }

  private normalizeUuidFieldOnRecord(record: Record<string, any>): void {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(record, 'uuid')) {
      return;
    }

    record['uuid'] = this.coerceUuidToString(record['uuid']);
  }

  private coerceUuidToString(value: any): any {
    if (value === null || value === undefined || typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'bigint' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      const candidateValues = [
        value?.value,
        value?.uuid,
        value?.id,
      ];

      for (const candidate of candidateValues) {
        if (
          candidate !== null &&
          candidate !== undefined &&
          (typeof candidate === 'string' || typeof candidate === 'number' || typeof candidate === 'bigint' || typeof candidate === 'boolean')
        ) {
          return String(candidate);
        }
      }
    }

    return String(value);
  }

  private applyNumericFormattingOnSave(record: any): void {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return;
    }

    Object.keys(record).forEach((key) => {
      record[key] = this.roundNumericValueForSave(record[key]);
    });
  }

  private roundNumericValueForSave(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.roundNumericValueForSave(item));
    }

    if (typeof value === 'number') {
      return this.roundNumberToTwoDecimals(value);
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return value;
      }

      if (!this.isPlainNumericString(trimmedValue)) {
        return value;
      }

      const parsedValue = Number(trimmedValue);
      return Number.isFinite(parsedValue)
        ? this.roundNumberToTwoDecimals(parsedValue)
        : value;
    }

    if (typeof value !== 'object') {
      return value;
    }

    const roundedObject: Record<string, any> = {};
    Object.keys(value).forEach((key) => {
      roundedObject[key] = this.roundNumericValueForSave(value[key]);
    });

    return roundedObject;
  }

  private roundNumberToTwoDecimals(value: number): number {
    if (!Number.isFinite(value)) {
      return value;
    }

    if (Number.isInteger(value)) {
      return value;
    }

    return Number(value.toFixed(2));
  }

  private isPlainNumericString(value: string): boolean {
    return /^-?\d+(?:\.\d+)?$/.test(value);
  }

  private sanitizeEmptyValues(value: any): any {
    if (this.isBlankString(value)) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeEmptyValues(item));
    }

    if (!value || typeof value !== 'object') {
      return value;
    }

    const sanitized: Record<string, any> = {};
    Object.keys(value).forEach((key) => {
      sanitized[key] = this.sanitizeEmptyValues(value[key]);
    });

    return sanitized;
  }

  private isBlankString(value: any): value is string {
    return typeof value === 'string' && value.trim() === '';
  }

  private containsBlankString(value: any): boolean {
    if (this.isBlankString(value)) {
      return true;
    }

    if (Array.isArray(value)) {
      return value.some((item) => this.containsBlankString(item));
    }

    if (!value || typeof value !== 'object') {
      return false;
    }

    return Object.keys(value).some((key) => this.containsBlankString(value[key]));
  }

  private resolveSelectedEntityKeyForSave(): string | null {
    const component = this as any;
    const globalFilters = component.globalFilterService.getCurrentFilters?.();
    const candidates = [
      this.normalizeFilterCandidate(globalFilters?.queryParams?.entity),
      this.normalizeFilterCandidate(globalFilters?.requestParams?.entity),
      component.selectedEntityValue,
      component.editableRecord?.entity,
      component.originalRecord?.entity,
      component.data?.requestData?.entity,
      component.data?.entityName,
      component.data?.entity,
    ];

    for (const candidate of candidates) {
      if (candidate !== null && candidate !== undefined) {
        const normalized = String(candidate).trim();
        if (normalized) {
          return normalized;
        }
      }
    }

    return null;
  }

  private normalizeFilterCandidate(value: any): any {
    if (Array.isArray(value)) {
      return value.length > 0 ? value[0] : null;
    }
    return value;
  }

  private executeConfiguredSaveRequest(
    apiUrl: string,
    filtersToSend: any,
    requestConfig: any,
    method: string,
  ): Observable<any> {
    const component = this as any;
    return new Observable((observer) => {
      component.configService.getApi(apiUrl, filtersToSend, requestConfig, method).subscribe({
        next: (response: any) => {
          const statusValue = typeof response?.status === 'string' ? response.status.toLowerCase() : null;
          const isSuccess = statusValue ? statusValue === 'success' : true;
          if (!isSuccess) {
            const message =
              response?.ui_response ||
              response?.error ||
              response?.detail ||
              'Save failed.';
            observer.error({
              type: 'business',
              response,
              message: String(message),
            });
            return;
          }

          observer.next(response);
          observer.complete();
        },
        error: (error: any) => observer.error(error),
      });
    });
  }

  private requiresSequentialFollowUpSave(selectedEntityKey: string | null): boolean {
    return !!getEntityFollowUpSaveHandler(selectedEntityKey);
  }

  private buildFollowUpSavePayload(
    saveResponse: any,
    saveTemplate: any,
    selectedEntityKey: string | null,
    action: any,
    precomputedResponseValue?: any,
  ): any {
    const component = this as any;
    const responseValue = precomputedResponseValue !== undefined
      ? precomputedResponseValue
      : this.extractFollowUpRequestValue(saveResponse);
    const followUpHandler = getEntityFollowUpSaveHandler(selectedEntityKey);
    const followUpPayload = saveTemplate
      ? JSON.parse(JSON.stringify(saveTemplate))
      : {
          requestData: {},
          responseList: [],
          entity: component.data?.entity || 'product',
          workflowId: component.data?.workflowId || 'UI',
          endpoints: [],
          communicationType: component.data?.communicationType || 'rest',
          version: component.data?.version || action?.version || '1.0',
        };

    if (followUpHandler?.buildPayload) {
      return this.normalizeUuidFieldsForPayload(followUpHandler.buildPayload({
        action,
        componentData: component.data,
        responseValue,
        saveResponse,
        saveTemplate,
        selectedEntityKey,
      }));
    }

    followUpPayload.requestData = {
      ...(followUpPayload.requestData || {}),
      entity: selectedEntityKey || followUpPayload.requestData?.entity || '',
      ...(responseValue && typeof responseValue === 'object' ? responseValue : {}),
    };
    if (followUpPayload?.requestData && Object.prototype.hasOwnProperty.call(followUpPayload.requestData, 'value')) {
      delete followUpPayload.requestData.value;
    }
    followUpPayload.requestData = this.normalizeUuidFieldsForPayload(followUpPayload.requestData);
    followUpPayload.responseList = [];
    followUpPayload.operation = null;
    followUpPayload.functionName = followUpHandler?.followUpFunctionName || followUpPayload.functionName;
    followUpPayload.entity = followUpPayload.entity || component.data?.entity || 'product';
    followUpPayload.workflowId = followUpPayload.workflowId || component.data?.workflowId || 'UI';
    followUpPayload.communicationType = followUpPayload.communicationType || 'rest';
    followUpPayload.version = followUpPayload.version || action?.version || component.data?.version || '1.0';

    if (Array.isArray(followUpPayload.endpoints) && followUpPayload.endpoints.length > 0) {
      followUpPayload.endpoints = followUpPayload.endpoints.map((endpoint: any, index: number) => {
        if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
          return endpoint;
        }

        return {
          ...endpoint,
          functionName: followUpHandler?.followUpFunctionName || followUpPayload.functionName,
        };
      });
    } else {
      followUpPayload.endpoints = [
        {
          functionName: followUpHandler?.followUpFunctionName || followUpPayload.functionName,
          version: followUpPayload.version || '1.0',
        },
      ];
    }

    return followUpPayload;
  }

  private extractFollowUpRequestValue(saveResponse: any): any {
    const responseEntries = Array.isArray(saveResponse?.responseList) ? saveResponse.responseList : [];

    for (const responseEntry of responseEntries) {
      if (!responseEntry || typeof responseEntry !== 'object') {
        continue;
      }

      for (const value of Object.values(responseEntry)) {
        if (!Array.isArray(value) || value.length === 0) {
          continue;
        }

        const extractedItems = value
          .map((item: any) => item?.data)
          .filter((item: any) => item !== null && item !== undefined);

        if (!extractedItems.length) {
          continue;
        }

        return extractedItems.length === 1 ? extractedItems[0] : extractedItems;
      }
    }

    return {};
  }

  private finishSuccessfulSave(action: any, selectedEntityKey: string | null, isNewRecord: boolean): void {
    const component = this as any;
    component.isSaving = false;
    component.snackBar.open(
      'Saved successfully!',
      'Close',
      this.getBottomRightSnackBarConfig(10000, ['success-snackbar']),
    );
    component.originalRecord = component.cloneRecord(component.editableRecord);
    this.dispatchPostSaveRefreshRequests(action, selectedEntityKey, {
      isNewRecord,
      recordSnapshot: component.cloneRecord(component.editableRecord),
    });
    if (action.refreshWidgets && Array.isArray(action.refreshWidgets)) {
      action.refreshWidgets.forEach((widgetId: string) => {
        component.globalFilterService.requestWidgetRefresh(widgetId);
      });
    }
  }

  private debugLog(event: string, details: any): void {
    console.log(`[${SideEntityFormComponent.DEBUG_LOG_KEY}] ${event}`, details);
  }

  private dispatchPostSaveRefreshRequests(
    action: any,
    selectedEntityKey: string | null,
    saveContext: { isNewRecord: boolean; recordSnapshot: any },
  ): void {
    type PostSaveRefreshRequest = {
      widgetId: string;
      requestData: any;
      apiUrl: string;
      method: string;
      saveContext?: {
        isNewRecord: boolean;
        recordSnapshot: any;
      };
    };

    const refreshRequests = Array.isArray(action?.refreshRequests) ? action.refreshRequests : [];
    refreshRequests
      .map((request: any) => this.normalizePostSaveRefreshRequest(request, selectedEntityKey, saveContext))
      .filter((request: PostSaveRefreshRequest | null): request is PostSaveRefreshRequest =>
        !!request?.widgetId && !!request?.requestData && !!request?.apiUrl)
      .forEach((request: PostSaveRefreshRequest) => {
        this.executePostSaveRefreshRequest(request);
      });
  }

  private normalizePostSaveRefreshRequest(
    request: any,
    selectedEntityKey: string | null,
    saveContext?: { isNewRecord: boolean; recordSnapshot: any },
  ): {
    widgetId: string;
    requestData: any;
    apiUrl: string;
    method: string;
    saveContext?: { isNewRecord: boolean; recordSnapshot: any };
  } | null {
    if (!request || typeof request !== 'object') {
      return null;
    }

    const widgetId = String(request?.widgetId || request?.id || '').trim();
    const apiUrl = String(request?.apiUrl || request?.url || this.data?.apiUrl || '/spriced/platform').trim();
    const method = String(request?.method || 'POST').trim() || 'POST';
    const requestDataTemplate =
      request?.requestData ??
      request?.payload ??
      request?.rawPayload ??
      request?.customPayload;

    if (!widgetId || !apiUrl || !requestDataTemplate || typeof requestDataTemplate !== 'object') {
      return null;
    }

    const requestData = JSON.parse(JSON.stringify(requestDataTemplate));
    if (requestData?.requestData && selectedEntityKey) {
      requestData.requestData.entity = selectedEntityKey;
    }
    this.applyPostSaveRefreshOrderByOverride(requestData);

    return {
      widgetId,
      requestData,
      apiUrl,
      method,
      saveContext,
    };
  }

  private executePostSaveRefreshRequest(
    request: {
      widgetId: string;
      requestData: any;
      apiUrl: string;
      method: string;
      saveContext?: { isNewRecord: boolean; recordSnapshot: any };
    },
  ): void {
    const component = this as any;
    const requestConfig = {
      ...component.data,
      apiUrl: request.apiUrl,
      method: request.method,
      rawPayload: request.requestData,
      customPayload: request.requestData,
      skipDefaultPayload: true,
      embedQueryParams: false,
      embeddedParams: [],
      useQueryParam: false,
    };

    component.configService.getApi(
      request.apiUrl,
      {},
      requestConfig,
      request.method,
    ).subscribe({
      next: (response: any) => {
        this.debugLog('post_save_refresh_success', {
          widgetId: request.widgetId,
          requestData: request.requestData,
          response,
        });
        window.dispatchEvent(new CustomEvent(SideEntityFormComponent.TABLE_REFRESH_EVENT, {
          detail: {
            widgetId: request.widgetId,
            response,
            requestData: request.requestData,
            saveContext: request.saveContext,
          },
        }));
      },
      error: (error: any) => {
        console.error('Post-save refresh failed', error);
        this.debugLog('post_save_refresh_error', {
          widgetId: request.widgetId,
          requestData: request.requestData,
          error,
        });
      },
    });
  }

  private applyPostSaveRefreshOrderByOverride(requestData: any): void {
    const filters = requestData?.requestData?.filters;
    if (!Array.isArray(filters)) {
      return;
    }

    filters.forEach((filter: any) => {
      const attributeName = String(filter?.attributeName || '')
        .trim()
        .toLowerCase();
      if (attributeName === 'order by') {
        filter.attributeValue = 'created_date desc';
      }
    });
  }

  private filterHiddenFormFields(): void {
    if (!Array.isArray((this as any).formFields)) {
      return;
    }

    const entityHiddenTokens = this.resolveEntityHiddenFieldTokens();

    (this as any).formFields = (this as any).formFields.filter((field: any) => {
      const candidates = [
        field?.key,
        field?.label,
      ];

      if (candidates.some((candidate) => this.isHiddenFieldToken(candidate))) {
        return false;
      }

      if (
        entityHiddenTokens &&
        candidates.some((candidate) => entityHiddenTokens.has(this.normalizeHiddenFieldToken(candidate)))
      ) {
        return false;
      }

      return true;
    });
  }

  private resolveEntityHiddenFieldTokens(): Set<string> | null {
    const entityKey = this.resolveSelectedEntityKeyForHeaderOrder();
    if (!entityKey) {
      return null;
    }

    return SideEntityFormComponent.ENTITY_HIDDEN_FIELD_TOKENS.get(entityKey.toLowerCase()) ?? null;
  }

  private applyKnownFieldOrderFallback(): void {
    if (!Array.isArray((this as any).formFields) || !(this as any).formFields.length) {
      return;
    }

    const normalizedLabels = new Set<string>(
      (this as any).formFields.map((field: any) => this.normalizeHiddenFieldToken(field?.label || field?.key)),
    );
    const fieldOrderMap = this.resolveKnownFieldOrderMap(normalizedLabels);
    if (!fieldOrderMap) {
      return;
    }

    (this as any).formFields = [...(this as any).formFields].sort((left: any, right: any) => {
      const leftOrder = this.resolveKnownFieldOrderIndex(left, fieldOrderMap);
      const rightOrder = this.resolveKnownFieldOrderIndex(right, fieldOrderMap);

      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder;
      }

      if (leftOrder !== undefined) {
        return -1;
      }

      if (rightOrder !== undefined) {
        return 1;
      }

      return 0;
    });
  }

  private isHiddenFieldToken(value: any): boolean {
    const normalizedValue = this.normalizeHiddenFieldToken(value);
    return SideEntityFormComponent.ALWAYS_HIDDEN_FIELD_TOKENS.has(normalizedValue) ||
      SideEntityFormComponent.HIDDEN_FIELD_TOKENS.has(normalizedValue);
  }

  private resolveKnownFieldOrderMap(normalizedLabels: Set<string>): Map<string, number> | null {
    const knownFieldOrderMaps = [
      SideEntityFormComponent.FUEL_RATING_DEFINITION_FIELD_ORDER,
      SideEntityFormComponent.RER_PA_HISTORY_FIELD_ORDER,
    ];

    for (const fieldOrderMap of knownFieldOrderMaps) {
      const expectedLabels = Array.from(fieldOrderMap.keys());
      const hasAllExpectedFields = expectedLabels.every((label) => normalizedLabels.has(label));
      if (hasAllExpectedFields) {
        return fieldOrderMap;
      }
    }

    return null;
  }

  private resolveKnownFieldOrderIndex(field: any, fieldOrderMap: Map<string, number>): number | undefined {
    const candidates = [
      field?.label,
      field?.key,
    ];

    for (const candidate of candidates) {
      const normalized = this.normalizeHiddenFieldToken(candidate);
      const matchedOrder = fieldOrderMap.get(normalized);
      if (matchedOrder !== undefined) {
        return matchedOrder;
      }
    }

    return undefined;
  }

  private normalizeHiddenFieldToken(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private formatAttributeLabel(option: any): string {
    if (option === null || option === undefined) {
      return '';
    }

    if (typeof option !== 'object') {
      return this.rewriteAttributeLabelString(String(option));
    }

    const data = option?.data ?? option;
    const code = data?.code ?? option?.code;
    const name =
      data?.name ??
      data?.display_name ??
      data?.displayName ??
      option?.name ??
      option?.display_name ??
      option?.displayName;

    if (code !== undefined && code !== null && name !== undefined && name !== null) {
      return `${code} {${name}}`;
    }

    const rawLabel =
      option?.label ??
      data?.label ??
      data?.name ??
      data?.display_name ??
      data?.displayName ??
      '';

    return this.rewriteAttributeLabelString(String(rawLabel || ''));
  }

  private rewriteDropdownOptionsForField(fieldKey: string, options: any[]): any[] {
    if (!Array.isArray(options)) {
      return options;
    }

    return options.map((option: any) => {
      const formattedLabel = this.formatAttributeLabel(option);
      if (!formattedLabel) {
        return option;
      }

      return {
        ...option,
        data: option?.data ?? option?.source ?? option,
        label: formattedLabel,
      };
    });
  }

  private normalizeDropdownOptionsForField(response: any, fieldKey: string): any[] {
    const rows = this.extractDropdownRowsFromResponse(response, fieldKey);
    const seen = new Set<string>();
    const options: any[] = [];

    rows.forEach((row: any) => {
      const normalizedOption = this.normalizeDropdownOptionForField(row, fieldKey);
      if (!normalizedOption) {
        return;
      }

      const dedupeKey = this.getComparableDropdownValue(normalizedOption.value);
      if (seen.has(dedupeKey)) {
        return;
      }

      seen.add(dedupeKey);
      options.push(normalizedOption);
    });

    return this.rewriteDropdownOptionsForField(fieldKey, options);
  }

  private extractDropdownRowsFromResponse(response: any, fieldKey: string): any[] {
    const rawRows =
      response?.responseList?.[0]?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[0]?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[fieldKey] ||
      response?.responseList?.[0]?.data?.[0]?.data ||
      response?.responseList?.[0]?.data ||
      response?.data?.[0]?.data ||
      response?.data ||
      response?.options ||
      [];

    return Array.isArray(rawRows) ? rawRows : [];
  }

  private normalizeDropdownOptionForField(option: any, fieldKey: string): { label: string; value: any; data: any } | null {
    if (option === null || option === undefined) {
      return null;
    }

    if (typeof option !== 'object') {
      const text = this.rewriteAttributeLabelString(String(option));
      return {
        label: text,
        value: option,
        data: option,
      };
    }

    const dropdownConfig = (this as any).getFieldDropdownConfig(fieldKey);
    const value = this.resolveDropdownOptionValue(option, dropdownConfig);
    const formattedLabel = this.formatAttributeLabel(option);
    const fallbackLabel = this.resolveDropdownOptionLabel(option, dropdownConfig);
    const label = formattedLabel || fallbackLabel;

    if (value === undefined || label === undefined || label === null || label === '') {
      return null;
    }

    return {
      label: String(label),
      value,
      data: option?.data ?? option,
    };
  }

  private resolveDropdownOptionValue(option: any, dropdownConfig?: any): any {
    const data = option?.data ?? option;
    const explicitValueKeys = [
      dropdownConfig?.dropdownValueKey,
      dropdownConfig?.valueKey,
      dropdownConfig?.idKey,
    ]
      .map((key: any) => String(key || '').trim())
      .filter(Boolean);

    for (const key of explicitValueKeys) {
      const resolved = data?.[key] ?? option?.[key];
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return resolved;
      }
    }

    return (
      data?.id ??
      option?.id ??
      data?.code ??
      option?.code ??
      data?.value ??
      option?.value ??
      data?.name ??
      option?.name ??
      option
    );
  }

  private resolveDropdownOptionLabel(option: any, dropdownConfig?: any): string {
    const data = option?.data ?? option;
    const explicitLabelKeys = [
      dropdownConfig?.dropdownLabelKey,
      dropdownConfig?.labelKey,
      dropdownConfig?.displayKey,
      dropdownConfig?.textKey,
    ]
      .map((key: any) => String(key || '').trim())
      .filter(Boolean);

    for (const key of explicitLabelKeys) {
      const resolved = data?.[key] ?? option?.[key];
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return this.rewriteAttributeLabelString(String(resolved));
      }
    }

    const fallback =
      data?.label ??
      option?.label ??
      data?.display_name ??
      option?.display_name ??
      data?.displayName ??
      option?.displayName ??
      data?.name ??
      option?.name ??
      data?.code ??
      option?.code ??
      data?.id ??
      option?.id ??
      data?.value ??
      option?.value ??
      '';

    return this.rewriteAttributeLabelString(String(fallback || ''));
  }

  private getComparableDropdownValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    const data = value?.data ?? value;
    return String(
      data?.id ??
      value?.id ??
      data?.code ??
      value?.code ??
      data?.value ??
      value?.value ??
      data?.name ??
      value?.name ??
      JSON.stringify(value),
    );
  }

  private rewriteAttributeLabelString(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    const hyphenMatch = normalizedValue.match(/^([^{}]+?)\s+-\s+([^{}]+)$/);
    if (hyphenMatch) {
      const left = hyphenMatch[1]?.trim();
      const right = hyphenMatch[2]?.trim();
      if (left && right) {
        return `${left} {${right}}`;
      }
    }

    return normalizedValue.replace(/^([^{}]+?)\s*\{(.+)\}$/, '$1 {$2}');
  }

}
